import { Col, Row } from 'antd';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useIsTeamMember from '../../../../../../hooks/useIsTeamMember';
import { RootState } from '../../../../../../store/rootReducer';
import HSButton from '../../../../../common/Button';
import NormalInput from '../../../../../common/NormalInput';
import { KYCDoc } from '../../../../../login/redux/types';
import { InvestorStatus } from '../../../../../login/utils/Status';
import KYCSubmitModal from '../../../../settings/modal/KYCSubmitModal';
import { DocSection, DocTooltip, SkipKYC } from '../Manual/IndividualKYC';
import KYCThankYou from '../ThankYou';

type Props = {
    isSetting?: boolean;
    onClickCancel?: () => void;
};

const SinpassForm: React.FC<Props> = ({ isSetting, onClickCancel }) => {
    const isTeamMember: boolean = useIsTeamMember();
    const DialCodes = useSelector((state: RootState) => state.constants.constants.countries) || [];
    const kyc = useSelector((state: RootState) => state.user.investor?.kyc);
    const investorStatus = useSelector((state: RootState) => state.user.investor?.status?.kyc);
    const kycDocs = useSelector((state: RootState) => state.user.investor?.kyc?.docs);
    const investor = useSelector((state: RootState) => state.user.investor);
    const HIPL_KYC_status = investor?.kyc?.reviewsV2?.find((i) => i.entity === 'HIPL')?.status;
    const showHIPLBanner =
        investor?.isHIPL &&
        (HIPL_KYC_status === InvestorStatus.PendingKYCAdditionalDetails ||
            HIPL_KYC_status === InvestorStatus.PendingKYCSubmission);

    const disabled =
        investorStatus !== InvestorStatus.PendingKYCSubmission &&
        investorStatus !== InvestorStatus.PendingKYCAdditionalDetails &&
        investorStatus !== InvestorStatus.PendingKYCResubmission &&
        !showHIPLBanner;

    let wealthDocs: KYCDoc[] = [];

    if (kycDocs && kycDocs.length > 0) {
        wealthDocs = kycDocs.filter((k) => k.type === 'Wealth Proof');
    }

    const [formFields, setFormFields] = useState<FormField>({
        legalName: undefined,
        address: undefined,
        address2: undefined,
        country: undefined,
        idNumber: undefined,
        idType: undefined,
        nationality: undefined,
        residentialStatus: undefined,
        zipCode: undefined,
        isValidate: false,
        aliasName: undefined,
        birthCountry: undefined,
        dob: undefined,
        gender: undefined,
        hanyuPinyinAliasName: undefined,
        hanyuPinyinName: undefined,
        marriedName: undefined,
        noa: undefined,
    });
    const [isCompleted, setCompleted] = useState(false);

    useLayoutEffect(() => {
        if (kyc && kyc.info) {
            setFormFields({
                legalName: kyc.info.legalName,
                address: kyc.info.address?.address1,
                address2: kyc.info.address?.address2,
                country: kyc.info.address?.country,
                idNumber: kyc.info.identity?.identityNumber,
                idType: kyc.info.identity?.identityType,
                nationality: kyc.info.nationality,
                residentialStatus: kyc.info.residentialStatus,
                zipCode: kyc.info.address?.postalCode,
                noa: kyc.info.noa,
                dob: kyc.info.dob,
                aliasName: kyc.info.aliasName,
                birthCountry: kyc.info.birthCountry,
                gender: kyc.info.gender,
                hanyuPinyinAliasName: kyc.info.hanyuPinyinAliasName,
                hanyuPinyinName: kyc.info.hanyuPinyinName,
                isValidate: false,
            });
        }
    }, [kyc]);

    const countrys: { label: string; value: string }[] = [];
    DialCodes.forEach((c) => {
        countrys.push({
            label: c.name,
            value: c.name,
        });
    });

    const form = useMemo(() => {
        const onFieldChange = (data: { name: string; value?: number | string }) => {
            const { name, value } = data;
            let obj = { ...formFields };
            if (name) {
                obj[name] = (value as string) || '';
            }
            setFormFields(obj);
        };

        return (
            <div>
                <p className="page_title_onboarding my-4">Personal Information</p>
                <Row gutter={[16, 16]}>
                    <Col xs={24}>
                        <NormalInput
                            title="Legal name"
                            isRequired
                            value={formFields.legalName}
                            name="legalName"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="ID Type"
                            isRequired
                            value={formFields.idType}
                            name="idType"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Identity Number"
                            isRequired
                            value={formFields.idNumber}
                            name="idNumber"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Date Of Birth"
                            isRequired
                            value={formFields.dob}
                            name="dob"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Gender"
                            isRequired
                            value={formFields.gender}
                            name="gender"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Alias name"
                            isRequired
                            value={formFields.aliasName}
                            name="aliasName"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Hanyu Pinyin Name"
                            isRequired
                            value={formFields.hanyuPinyinName}
                            name="hanyuPinyinName"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Hanyu Pinyin Alias Name"
                            isRequired
                            value={formFields.hanyuPinyinAliasName}
                            name="hanyuPinyinAliasName"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Married Name"
                            isRequired
                            value={formFields.marriedName}
                            name="marriedName"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Country/Place of Birth"
                            isRequired
                            value={formFields.birthCountry}
                            name="birthCountry"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Singapore Residential Status"
                            value={formFields.residentialStatus}
                            name="residentialStatus"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Nationality"
                            isRequired
                            value={formFields.nationality}
                            name="nationality"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                </Row>
                <p className="page_title_onboarding my-4">Contact Information</p>
                <Row gutter={[16, 16]}>
                    <Col xs={24}>
                        <NormalInput
                            title="Address"
                            isRequired
                            value={formFields.address}
                            name="address"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24}>
                        <NormalInput
                            title="Address line 2"
                            value={formFields.address2}
                            name="address2"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Country"
                            isRequired
                            value={formFields.country}
                            name="country"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Zip code"
                            isRequired
                            type="number"
                            value={formFields.zipCode}
                            name="zipCode"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled
                        />
                    </Col>
                </Row>
            </div>
        );
    }, [formFields]);

    return (
        <>
            {!isCompleted ? (
                <>
                    <p className="text-xl Canela-Regular text-center py-8">Information from your Singpass</p>
                    {form}

                    {(!formFields.noa || !formFields.noa.amount) && (
                        <>
                            <p className="page_title_onboarding my-4">KYC Documents</p>
                            <DocSection
                                title="Proof of Income or Net Assets"
                                docs={wealthDocs}
                                docType="Wealth Proof"
                                required
                                tooltip={DocTooltip.wealthProof}
                                disabled={disabled || isTeamMember}
                            />
                        </>
                    )}
                    {formFields.noa && formFields.noa.amount && (
                        <>
                            <p className="page_title_onboarding my-4"> Annual Income (Notice of Assessment)</p>
                            <Row>
                                <Col xs={24} md={24}>
                                    <div className="flex">
                                        <NormalInput
                                            title="Year"
                                            type="number"
                                            value={formFields.noa?.yearofAssessment}
                                            name="yearofAssessment"
                                            onChange={(data: { name: string; value?: number | string }) => {
                                                const { name, value } = data;
                                                let obj = { ...formFields };
                                                if (name) {
                                                    obj[name] = (value as string) || '';
                                                }
                                                setFormFields(obj);
                                            }}
                                            className="rounded-xl"
                                            disabled
                                            style={{ minHeight: '3.2rem' }}
                                        />
                                        <div className="w-3.5" />
                                        <NormalInput
                                            title="Income"
                                            type="number"
                                            value={formFields.noa?.amount.amountUpto2dp}
                                            name="zipCode"
                                            prefix={
                                                <p className="text-gray-400 mr-1">{formFields.noa?.amount.currency}</p>
                                            }
                                            onChange={(data: { name: string; value?: number | string }) => {
                                                const { name, value } = data;
                                                let obj = { ...formFields };
                                                if (name) {
                                                    obj[name] = (value as string) || '';
                                                }
                                                setFormFields(obj);
                                            }}
                                            className="rounded-xl"
                                            disabled
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                    <div
                        className={`flex justify-center items-center mt-10 mb-5 ${isSetting ? 'flex-row' : 'flex-col'}`}
                    >
                        {!disabled && (
                            <KYCSubmitModal
                                isDisable={!kyc?.info.noa?.amount?.amountUpto2dp && wealthDocs.length === 0}
                                // className="onboarding_btn"
                                onSubmitCallback={() => {
                                    if (!isSetting) {
                                        setCompleted(true);
                                    } else if (onClickCancel) {
                                        onClickCancel();
                                    }
                                }}
                                kycInfo={formFields}
                                myinfo={true}
                            />
                        )}
                        {isSetting && onClickCancel && (
                            <HSButton className="onboarding_btn ml-2" light title="Cancel" onClick={onClickCancel} />
                        )}
                        {!isSetting && <SkipKYC disabled={disabled} />}
                    </div>
                </>
            ) : (
                <KYCThankYou />
            )}
        </>
    );
};

export default SinpassForm;

interface FormField {
    [k: string]:
        | string
        | boolean
        | undefined
        | {
              amount: {
                  amountUpto2dp: number;
                  currency: string;
              };
              yearofAssessment: string;
          };
    legalName?: string;
    idType?: string;
    idNumber?: string;
    address?: string;
    country?: string;
    zipCode?: string;
    residentialStatus?: string;
    nationality?: string;
    isValidate: boolean;
    address2?: string;
    dob?: string;
    gender?: string;
    aliasName?: string;
    hanyuPinyinName?: string;
    hanyuPinyinAliasName?: string;
    marriedName?: string;
    birthCountry?: string;
    noa?: {
        amount: {
            amountUpto2dp: number;
            currency: string;
        };
        yearofAssessment: string;
    };
}
